import {
  BaseFilterCriteriaInfo,
  DataTable2,
  FieldSet,
  IDataTableColumn,
  IDataTableProps,
  IFieldSetProps,
  ISearchPanelProps,
  SearchPanel,
  SortKeysObject,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";
import { useLocation } from "react-router";
import styled from "styled-components";

import { ICustomBreadcrumbProps, PageBase } from "../";
import { useEventsContext } from "../../contexts";
import { useCommonHooks, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { formatDate } from "../../utils/formatDate";

interface ISearchTablePageProps<TResults>
  extends Pick<
      IDataTableProps<any>,
      "customizeRowStyle" | "onRowClick" | "onOpenSubComponent"
    >,
    Pick<IFieldSetProps, "rightElement" | "buttons">,
    Pick<
      ISearchPanelProps<any>,
      "overrideListValues" | "defaultCriterias" | "storageMode"
    > {
  searchFunction: (sObj: any) => TResults | Promise<TResults>;
  getCriteriasFunction?: () =>
    | BaseFilterCriteriaInfo[]
    | Promise<BaseFilterCriteriaInfo[]>;
  columns: Array<IDataTableColumn>;
  breadCrumbs?: ICustomBreadcrumbProps[];
  criteriasTlPrefix?: string;
  withCard?: boolean;
  sortKeys?: SortKeysObject;
  title?: string;
  enablePagination?: boolean;
  allowOverflow?: boolean;
  displayBottomTitle?: boolean;
  addButtonOnClick?: () => void;
  onOpenSubComponent?: (item: any) => React.ReactElement | string;
  defaultPageSize?: number;
}

const Container = styled.div`
  & > * + * {
    margin-top: 0.5rem;
  }
`;

export function SearchTablePage<TResults>({
  searchFunction,
  getCriteriasFunction,
  columns,
  breadCrumbs,
  withCard = true,
  criteriasTlPrefix,
  overrideListValues,
  sortKeys = {},
  defaultCriterias = [],
  rightElement,
  customizeRowStyle,
  title,
  onRowClick,
  enablePagination = true,
  allowOverflow = true,
  displayBottomTitle = false,
  defaultPageSize = 50,
  storageMode = "sessionStorage",
}: ISearchTablePageProps<TResults>) {
  const { t, tUnsafe } = useTl();
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: defaultPageSize,
    sortKeys: sortKeys,
  });
  const { totalCount } = tableState;

  const location = useLocation();
  const { router, currentLanguage } = useCommonHooks();

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: !!!getCriteriasFunction,
  });

  const OnRefreshSearchTablesEvent = React.useCallback(() => {
    search();
  }, [search]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
    return () =>
      unsubscribeEvent("RefreshSearchTables", OnRefreshSearchTablesEvent);
  }, [OnRefreshSearchTablesEvent, subscribeToEvent, unsubscribeEvent]);

  const finalSearchCriteriaFunction = React.useMemo(() => {
    if (getCriteriasFunction || currentLanguage) {
      return () => getCriteriasFunction();
    }
  }, [currentLanguage, getCriteriasFunction]);
  return (
    <PageBase
      withCard={withCard}
      breadCrumbs={breadCrumbs}
      allowOverflow={allowOverflow}
    >
      <Container>
        {getCriteriasFunction && (
          <FieldSet title={title ?? t(ETLCodes.TableCriterias)}>
            <SearchPanel
              getCriteriasFunc={finalSearchCriteriaFunction}
              defaultCriterias={defaultCriterias}
              onSearch={search}
              enableOrOnSameCriteria={true}
              translateFunc={tUnsafe}
              tlDataPrefix={criteriasTlPrefix}
              overrideListValues={overrideListValues}
              triggerInitialSearch={true}
              criteriaIntent="primary"
              storageMode={storageMode}
              storageKey={"search_" + location.pathname}
            />
          </FieldSet>
        )}
        <FieldSet
          title={
            displayBottomTitle && title
              ? title
              : t(ETLCodes.TableResults, { count: totalCount })
          }
          rightElement={rightElement}
        >
          <DataTable2
            tableState={tableState}
            loading={loading}
            columns={columns}
            showColumnSelector={false} //Warning, column selector doesn't work
            customizeRowStyle={customizeRowStyle}
            onRowClick={onRowClick}
            filterMode="OnEnter"
            translateFunc={tUnsafe}
            formatDate={formatDate}
          />
        </FieldSet>
      </Container>
    </PageBase>
  );
}
